import { useEffect, useRef, useState } from "react";
import { useLazyGetCollectionsSummaryQuery } from "../collections/collectionsApi";
import { useLazyListAttendanceAssignmentsQuery } from "../guarding/guardingApi";
import { useLazyListCollectorAllocationsQuery, firstName } from "../collections/collectorListsApi";

// Collections only became the system of record in September 2026 - a month
// before that has no entries for ANYONE, so it can never count as "missed".
const FIRST_MONTH = { year: 2026, month: 8 }; // JS month index: 8 = September
const MAX_MONTHS = 12; // one summary call per month - keep the fan-out bounded

// Texas's own premises/office staff are modelled as customer_assmnt slots under
// internal "customers" - they hold guard posts but never pay. Keyed by
// branch-custId because cust_id is only unique within a branch.
const INTERNAL_CUSTOMER_KEYS = new Set(["1-393", "1-394", "1-483", "1-647", "2-3569"]);

const pad = (n) => String(n).padStart(2, "0");
const isoDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const buildMonths = (today) => {
  const months = [];
  let cursor = new Date(FIRST_MONTH.year, FIRST_MONTH.month, 1);
  while (cursor <= today) {
    const last = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
    const isCurrent = cursor.getFullYear() === today.getFullYear() && cursor.getMonth() === today.getMonth();
    months.push({
      key: `${cursor.getFullYear()}-${pad(cursor.getMonth() + 1)}`,
      label: cursor.toLocaleString("en-GB", { month: "short", year: "2-digit" }),
      dateFrom: isoDate(cursor),
      dateTo: isoDate(isCurrent ? today : last),
      isCurrent,
    });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
  }
  return months.slice(-MAX_MONTHS);
};

/**
 * Customers holding active guard posts, against the months they have a
 * collection entry for. Pure client-side join of two existing reads:
 *
 *  - /attendance/dashboard/assignments (Day + Night) is the only endpoint that
 *    returns every active duty slot across customers, so the slot count per
 *    customer IS the guard count.
 *  - /collections/summary?groupBy=customer, once per month - its groupKey is
 *    "{customerBranchId}-{custId}", the same key built from the slots here, so
 *    nothing is ever matched by customer name.
 *
 * A month counts as paid only when money was actually received: a "Visit" entry
 * (amount 0) is a collector calling in, not a payment.
 *
 * With `withCollectors` (2026-09-22) each customer also carries the collector
 * list's view of them - /collections/collectors/allocations, one call - joined
 * on the same branch-custId key: `lastCollectedDate` (live receipts OR the
 * loaded pre-app history, whichever is later) and the current collector's
 * first name. A guarded customer nobody has ever collected from is simply
 * absent from that list, so both stay null and the card says so.
 */
export const useGuardedCustomerPayments = ({ branchId, skip = false, withCollectors = false } = {}) => {
  const [state, setState] = useState({ customers: [], months: [], isLoading: false, isError: false });
  const [fetchAssignments] = useLazyListAttendanceAssignmentsQuery();
  const [fetchSummary] = useLazyGetCollectionsSummaryQuery();
  const [fetchAllocations] = useLazyListCollectorAllocationsQuery();
  const requestId = useRef(0);

  useEffect(() => {
    if (skip) return;
    const thisRequest = ++requestId.current;

    (async () => {
      setState((s) => ({ ...s, isLoading: true, isError: false }));
      try {
        const months = buildMonths(new Date());
        const [daySlots, nightSlots, allocations, ...summaries] = await Promise.all([
          fetchAssignments({ shift: "Day" }, true).unwrap(),
          fetchAssignments({ shift: "Night" }, true).unwrap(),
          withCollectors ? fetchAllocations({ branchId }, true).unwrap() : Promise.resolve([]),
          ...months.map((m) =>
            fetchSummary({ branchId, dateFrom: m.dateFrom, dateTo: m.dateTo, groupBy: "customer" }, true).unwrap()
          ),
        ]);
        if (thisRequest !== requestId.current) return; // a newer request superseded this one

        const allocationByKey = new Map((allocations || []).map((a) => [`${a.customerBranchId}-${a.custId}`, a]));

        const byCustomer = new Map();
        const addSlots = (slots, field) =>
          (slots || []).forEach((slot) => {
            if (branchId != null && Number(slot.branchId) !== Number(branchId)) return;
            const key = `${slot.branchId}-${slot.custId}`;
            if (INTERNAL_CUSTOMER_KEYS.has(key)) return;
            if (!byCustomer.has(key)) {
              byCustomer.set(key, {
                key,
                branchId: slot.branchId,
                branchCode: slot.branchCode,
                custId: slot.custId,
                custName: slot.custName,
                dayGuards: 0,
                nightGuards: 0,
              });
            }
            byCustomer.get(key)[field] += 1;
          });
        addSlots(daySlots, "dayGuards");
        addSlots(nightSlots, "nightGuards");

        const paidKeysByMonth = summaries.map(
          (rows) => new Set((rows || []).filter((r) => Number(r.totalAmount) > 0).map((r) => r.groupKey))
        );

        const customers = [...byCustomer.values()].map((c) => {
          const unpaid = months.filter((m, i) => !paidKeysByMonth[i].has(c.key));
          const allocation = allocationByKey.get(c.key);
          return {
            ...c,
            totalGuards: c.dayGuards + c.nightGuards,
            missedMonths: unpaid.filter((m) => !m.isCurrent),
            isCurrentMonthDue: unpaid.some((m) => m.isCurrent),
            lastCollectedDate: allocation?.lastCollectedDate || null,
            collectorEmployeeRecordGuid: allocation?.collectorEmployeeRecordGuid || null,
            collectorFirstName: allocation?.collectorName ? firstName(allocation.collectorName) : null,
          };
        });

        setState({ customers, months, isLoading: false, isError: false });
      } catch (e) {
        if (thisRequest === requestId.current) setState({ customers: [], months: [], isLoading: false, isError: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId, skip, withCollectors]);

  return state;
};
